import { useState } from "react";
import { Crosshair, Loader2, MapPin, Send } from "lucide-react";
import { toast } from "react-toastify";

import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import {
  coordenadasDoLink,
  linkDoGoogleMaps,
  mapaEmbutido,
} from "../../utils/localizacao";

// Enviar localização para o cliente.
//
// A conversa já recebia mapa e não sabia mandar nenhum: para combinar um
// ponto de encontro, a saída era colar um link do Google Maps no meio do
// texto. Aqui sai como localização de verdade — o cartão que o WhatsApp abre
// no Waze ou no Maps com um toque.
//
// Três caminhos para dizer onde é: a localização do próprio computador, um
// link do Maps colado, ou as coordenadas digitadas. Antes de enviar, o mapa
// aparece na tela: mandar o ponto errado para um cliente é o tipo de erro que
// só se descobre quando ele chega ao lugar errado.
const EnviarLocalizacaoModal = ({ aberto, aoFechar, ticketId }) => {
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [nome, setNome] = useState("");
  const [endereco, setEndereco] = useState("");
  const [colado, setColado] = useState("");
  const [buscando, setBuscando] = useState(false);
  const [enviando, setEnviando] = useState(false);

  const lat = Number(String(latitude).replace(",", "."));
  const lon = Number(String(longitude).replace(",", "."));
  const valido =
    Number.isFinite(lat) &&
    Number.isFinite(lon) &&
    String(latitude).trim() !== "" &&
    String(longitude).trim() !== "" &&
    Math.abs(lat) <= 90 &&
    Math.abs(lon) <= 180;

  const usarMinhaLocalizacao = () => {
    if (!navigator.geolocation) {
      toast.error("Este navegador não sabe informar a localização.");
      return;
    }

    setBuscando(true);
    navigator.geolocation.getCurrentPosition(
      (posicao) => {
        setLatitude(String(posicao.coords.latitude));
        setLongitude(String(posicao.coords.longitude));
        setBuscando(false);
      },
      (erro) => {
        setBuscando(false);
        // O navegador só entrega a localização em https (ou em localhost) e
        // com permissão: dizer qual dos dois faltou poupa a investigação.
        toast.error(
          erro?.code === 1
            ? "Permissão de localização negada no navegador."
            : "Não foi possível obter a localização agora.",
        );
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  // Aceita tanto um link do Maps quanto "-15.79, -47.88" digitado à mão.
  const aplicarColado = (texto) => {
    setColado(texto);

    const doLink = coordenadasDoLink(texto);
    const soltas = texto.match(
      /(-?\d{1,3}(?:[.,]\d+)?)\s*,\s*(-?\d{1,3}(?:[.,]\d+)?)/,
    );

    const achado =
      doLink ||
      (soltas
        ? {
            latitude: Number(soltas[1].replace(",", ".")),
            longitude: Number(soltas[2].replace(",", ".")),
          }
        : null);

    if (achado) {
      setLatitude(String(achado.latitude));
      setLongitude(String(achado.longitude));
    }
  };

  const enviar = async () => {
    if (!valido) return;

    setEnviando(true);

    try {
      await api.post(`/messages/${ticketId}/location`, {
        latitude: lat,
        longitude: lon,
        nome: nome.trim() || undefined,
        endereco: endereco.trim() || undefined,
      });

      toast.success("Localização enviada.");
      aoFechar();
    } catch (err) {
      toastError(err);
    }

    setEnviando(false);
  };

  return (
    <Dialog open={aberto} onOpenChange={(estado) => !estado && aoFechar()}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-red-500" />
            Enviar localização
          </DialogTitle>
          <DialogDescription>
            Use a sua localização, cole um link do Google Maps ou digite as
            coordenadas.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <Button
            variant="outline"
            className="w-full gap-2"
            onClick={usarMinhaLocalizacao}
            disabled={buscando || enviando}
          >
            {buscando ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Crosshair className="h-4 w-4" />
            )}
            Usar a minha localização
          </Button>

          <div className="space-y-1">
            <Label htmlFor="local-colado">Link do mapa ou coordenadas</Label>
            <Input
              id="local-colado"
              value={colado}
              onChange={(e) => aplicarColado(e.target.value)}
              placeholder="https://maps.google.com/… ou -15.793889, -47.882778"
              disabled={enviando}
            />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label htmlFor="local-lat">Latitude</Label>
              <Input
                id="local-lat"
                value={latitude}
                onChange={(e) => setLatitude(e.target.value)}
                placeholder="-15.793889"
                inputMode="decimal"
                disabled={enviando}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="local-lon">Longitude</Label>
              <Input
                id="local-lon"
                value={longitude}
                onChange={(e) => setLongitude(e.target.value)}
                placeholder="-47.882778"
                inputMode="decimal"
                disabled={enviando}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label htmlFor="local-nome">Nome do lugar (opcional)</Label>
              <Input
                id="local-nome"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                placeholder="Loja Centro"
                disabled={enviando}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="local-endereco">Endereço (opcional)</Label>
              <Input
                id="local-endereco"
                value={endereco}
                onChange={(e) => setEndereco(e.target.value)}
                placeholder="Rua das Flores, 100"
                disabled={enviando}
              />
            </div>
          </div>

          {/* A prévia é a conferência: o cartão que o cliente vai receber. */}
          <div className="overflow-hidden rounded-lg border">
            {valido ? (
              <>
                <iframe
                  src={mapaEmbutido(lat, lon)}
                  title="Prévia da localização"
                  loading="lazy"
                  className="h-48 w-full border-0"
                />
                <div className="flex items-center justify-between gap-2 border-t px-3 py-2">
                  <span className="font-mono text-xs text-muted-foreground">
                    {lat.toFixed(6)}, {lon.toFixed(6)}
                  </span>
                  <a
                    href={linkDoGoogleMaps(lat, lon)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-primary hover:underline"
                  >
                    Conferir no Google Maps
                  </a>
                </div>
              </>
            ) : (
              <p className="p-6 text-center text-sm text-muted-foreground">
                Informe um ponto para ver o mapa aqui.
              </p>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={aoFechar} disabled={enviando}>
            Cancelar
          </Button>
          <Button onClick={enviar} disabled={!valido || enviando} className="gap-2">
            {enviando ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
            Enviar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EnviarLocalizacaoModal;
